import type { FederatedPointerEvent, Texture } from "pixi.js";
import type { CanvasTapRegistry } from "../spin/CanvasTapTargets";
import type { DeferredFeatureOfferPort } from "../feature-purchase/DeferredFeatureOfferPort";
import type { PlayerSpinGate } from "../spin/PlayerSpinGate";
import type { BetLinesService } from "../bet/BetLinesService";
import type { SlotApp } from "../app/createSlotApp";
import type { BootLaunchPayload } from "./BootLaunchPayload";
import type { CoreServices } from "./CoreServices";
import type { GameRuntime } from "../composition/GameRuntime";
import type { ShellChrome } from "./createShellChrome";
import type { GameChromeDefinition, GameFixturesDefinition } from "../composition/GameDefinition";
import { ControlBarPresenter } from "../controls/bar/ControlBarPresenter";
import { BetPresenter } from "../bet/BetPresenter";
import { SpinInputPresenter } from "../controls/spin/SpinInputPresenter";
import { SpinButtonPresenter } from "../controls/spin/SpinButtonPresenter";
import { FeatureModeUiPresenter } from "../feature-mode/FeatureModeUiPresenter";
import { GameHeaderPresenter } from "../controls/header/GameHeaderPresenter";
import { ShellInfoPagePresenter } from "../controls/info/ShellInfoPagePresenter";
import { GameHistoryPresenter } from "../game-history/GameHistoryPresenter";
import { ShellMenuPresenter } from "../controls/menu/ShellMenuPresenter";
import { FeaturePurchasePresenter } from "../feature-purchase/FeaturePurchasePresenter";
import { AutoSpinPresenter } from "../autospin/AutoSpinPresenter";
import { SpeedModePresenter } from "../controls/speed-mode/SpeedModePresenter";
import { BalancePresenter } from "../balance/BalancePresenter";
import { CurrentWinPresenter } from "../win/current-win/CurrentWinPresenter";
import { GambleEntryPresenter } from "../gamble/GambleEntryPresenter";
import { GridLoadingOverlayPresenter } from "../spin/loading-overlay/GridLoadingOverlayPresenter";
import { GridSlamTouchPresenter } from "../spin/GridSlamTouchPresenter";
import { BackendGameHistoryProvider } from "../game-history/BackendGameHistoryProvider";
import { replayLink } from "../game-history/replayLink";
import type { Log } from "@slot/presentation";

/**
 * The presenter tree over the built chrome, started: the bar's presenters (bet, spin, spin input,
 * feature mode, header, menu, info page, history, purchase, autospin, speed) under the control bar
 * presenter that spans them, then balance, current win, gamble entry, the grid loading overlay and
 * the tap-anywhere slam. Replay mode locks the bar down.
 */
export async function createControlBar(input: {
  slot: SlotApp;
  payload: BootLaunchPayload;
  services: CoreServices;
  chrome: ShellChrome;
  runtime: GameRuntime;
  gameChrome: GameChromeDefinition;
  fixtures: GameFixturesDefinition;
  replayMode: boolean;
  /** The title's canvas tap targets (decision 51): the tap surface offers them each tap before its slam. */
  canvasTaps: CanvasTapRegistry;
  /** The title's way to one offer's confirm (decision 51), bound here to the purchase flow when there is one. */
  featureOffers: DeferredFeatureOfferPort;
  /** A round waiting for the player's Spin (decision 55): the Spin button shows its spin face, and its press ends the wait. */
  playerSpins: PlayerSpinGate;
  /** The title's way to the bet's lines (decision 56): its choices are taken from here on, by the bet controls' rule. */
  betLines: BetLinesService;
  log: Log;
}): Promise<ControlBarPresenter> {
  const { slot, payload, services, chrome, runtime, replayMode, log } = input;
  const { controlBar: bar } = chrome;
  const settings = payload.initialSettings;
  const formatter = services.moneyContext.formatter;
  const spinCoordinator = runtime.getSpinCoordinator();
  input.betLines.bind(spinCoordinator);
  const featureModeMultipliersById = settings.featureModeMultipliersById ?? {};
  const gameHistoryEnabled = settings.gameHistoryEnabled ?? false;
  const offers = input.gameChrome.featurePurchaseOffers;

  // The presenters take the bar's rules as closures over the control bar presenter declared last.
  const bet = new BetPresenter({
    panel: bar.betPanel,
    betSystem: services.betSystem,
    audioService: services.audioService,
    formatter,
    featureModeStore: services.featureModeStore,
    featureModeMultipliersById,
    spinCoordinator,
    // A bet change never closes the feature purchase panel: its top bar carries the same controls.
    onBeforeBetChange: () => controlBar.closeOverlaysExceptFeaturePurchase(),
  });
  const spinInput = new SpinInputPresenter(
    bar.spinButton,
    {
      holdToSpinEnabled: settings.holdToSpinEnabled !== false,
      autoSpinStore: services.autoSpinStore,
      interactionBlockStore: services.interactionBlockStore,
      audioService: services.audioService,
      hapticsService: services.hapticsService,
      chromeTaskRunner: services.chromeTaskRunner,
      playerSpins: input.playerSpins,
    },
    spinCoordinator,
    () => controlBar.closeOverlays(),
  );
  const spinButton = new SpinButtonPresenter({
    panel: bar.spinButton,
    autoSpinStore: services.autoSpinStore,
    featureStateStore: services.featureStateStore,
    featureModeStore: services.featureModeStore,
    spinCoordinator,
    spinInputPresenter: spinInput,
    isMobileLayout: () => slot.viewport.getSnapshot().shape !== "desktop",
    playerSpins: input.playerSpins,
    featureHudInBar: input.gameChrome.featureHud !== false,
  });
  const featureModeUi = new FeatureModeUiPresenter(
    { featureModeStore: services.featureModeStore, betSystem: services.betSystem, chromeTaskRunner: services.chromeTaskRunner, localization: services.localization },
    { badgePanel: bar.featureModeBadge, disableButtonPanel: bar.featureModeDisable, betPanel: bar.betPanel },
  );
  const header = new GameHeaderPresenter(chrome.header, settings.gameTitle);
  const infoPage = new ShellInfoPagePresenter(
    chrome.infoPagePanel,
    { interactionBlockStore: services.interactionBlockStore, localization: services.localization, betSystem: services.betSystem, formatter, initialSettings: settings, gameVersion: payload.gameVersion, featurePurchaseOffers: offers, buildExtraFeatureAvailability: input.gameChrome.buildExtraFeatureAvailability, devModeEnabled: payload.devModeEnabled, log },
    input.gameChrome.buildExtraInfoTokens,
    payload.assets.resolveUrl(payload.sharedBundleName, "info/html/template"),
  );
  // History: the backend endpoint when a backend is configured, else the offline fixture rows.
  const gameHistory = gameHistoryEnabled
    ? new GameHistoryPresenter({
        panel: chrome.gameHistoryPanel,
        interactionBlockStore: services.interactionBlockStore,
        formatter,
        log,
        // This page's address with `replay` set and only the flags a replay may carry (decision 81): never the launch.
        buildReplayUrl: (roundId) => replayLink(window.location.href, roundId),
        // A new tab, where the history page has always opened a replay; `noopener`, so the replay (a second game) gets no `window.opener` handle on this real-money tab.
        openReplay: (replayUrl) => window.open(replayUrl, "_blank", "noopener"),
        historyProvider:
          payload.backendConfig.baseUrl.length > 0
            ? new BackendGameHistoryProvider({ baseUrl: payload.backendConfig.baseUrl, historyPath: payload.backendConfig.historyPath, playSession: payload.playSession })
            : input.fixtures.createHistoryProvider(),
      })
    : null;
  const menu = new ShellMenuPresenter(
    chrome.menu,
    bar.menuToggle,
    {
      audioSettingsStore: services.audioSettingsStore,
      audioSettingsService: services.audioSettingsService,
      hapticsSettingsStore: services.hapticsSettingsStore,
      interactionBlockStore: services.interactionBlockStore,
      gameHistoryEnabled,
      viewport: slot.viewport,
      onHome: () => log("[Menu] home requested — no lobby destination is configured"),
    },
    infoPage,
    spinCoordinator,
    () => controlBar.closeOverlays(),
    gameHistory,
  );
  // Feature purchase: the game's offers priced by the operator settings. Not in replay mode: the bar
  // hides the toggle, and the presenter's feature-mode subscriber would re-show it.
  const featurePurchase =
    !replayMode && offers.length > 0
      ? new FeaturePurchasePresenter({
          panel: chrome.featurePurchasePanel,
          toggleButton: bar.purchaseToggle,
          deps: { betSystem: services.betSystem, balanceStore: services.balanceStore, featureModeStore: services.featureModeStore, interactionBlockStore: services.interactionBlockStore, formatter, localization: services.localization, log },
          spinCoordinator,
          offers,
          featurePriceMultipliersById: settings.featurePriceMultipliersById ?? {},
          featureModeMultipliersById,
          loadIcon: async (path) => {
            const texture = payload.assets.getLoadedAssetOrNull<Texture>(payload.sharedBundleName, path);
            return texture ? chrome.images.textureToDataUrl(texture) : null;
          },
          // The purchase top bar's bet buttons are the control bar's bet steps: a bet change never closes the panel.
          betActions: {
            increase: () => {
              controlBar.closeOverlaysExceptFeaturePurchase();
              bet.increase();
            },
            decrease: () => {
              controlBar.closeOverlaysExceptFeaturePurchase();
              bet.decrease();
            },
          },
          onBuyOffer: (offerId) => {
            void runtime.buyFeature({ featureId: offerId });
          },
          onBeforeOpen: () => controlBar.closeOverlays(),
        })
      : null;
  if (featurePurchase) input.featureOffers.bind(featurePurchase);
  const autoSpin = new AutoSpinPresenter(
    {
      featureStateStore: services.featureStateStore,
      interactionBlockStore: services.interactionBlockStore,
      autospinEnabled: settings.autospinEnabled !== false,
      autospinPresets: settings.autospinPresets,
      viewport: slot.viewport,
      playerSpins: input.playerSpins,
    },
    spinCoordinator,
    services.autoSpinStore,
    { toggleButton: bar.autoSpinToggle, popup: chrome.autoSpinPopup, activeButton: bar.activeAutoSpin, onBeforeOpen: () => controlBar.closeOverlays() },
  );
  const speedMode = new SpeedModePresenter(
    {
      interactionBlockStore: services.interactionBlockStore,
      autoSpinStore: services.autoSpinStore,
      fastSpinEnabled: settings.fastSpinEnabled !== false,
      superFastSpinEnabled: settings.superFastSpinEnabled !== false,
      viewport: slot.viewport,
    },
    spinCoordinator,
    { toggle: bar.speedToggle, popup: chrome.speedPopup, onBeforeOpen: () => controlBar.closeOverlays() },
  );
  const controlBar = new ControlBarPresenter({
    bar,
    spinCoordinator,
    presenters: { bet, spinButton, spinInput, featureModeUi, header, menu, infoPage, gameHistory, featurePurchase, autoSpin, speedMode },
    deps: {
      betSystem: services.betSystem,
      featureStateStore: services.featureStateStore,
      featureModeStore: services.featureModeStore,
      autoSpinStore: services.autoSpinStore,
      formatter,
      viewport: slot.viewport,
      featureModeMultipliersById,
      playerSpins: input.playerSpins,
    },
    replayMode,
  });
  await controlBar.start();

  const balancePresenter = new BalancePresenter({ balanceStore: services.balanceStore, formatter, panel: bar.balance });
  balancePresenter.start();
  new CurrentWinPresenter({ formatter, store: services.currentWinStore, panel: bar.currentWin }).start();
  new GambleEntryPresenter(bar.gambleEntry, spinCoordinator).start();
  new GridLoadingOverlayPresenter(chrome.gridLoadingOverlay, spinCoordinator, services.chromeTaskRunner).start();
  // Tap anywhere not caught by a control slams (the full-screen surface under the bar).
  new GridSlamTouchPresenter({
    gridInput: {
      bindTap: (onTap) => {
        const surface = chrome.gridSlamOverlay;
        const handler = (event: FederatedPointerEvent): void => {
          if (surface.visible) onTap({ x: event.global.x, y: event.global.y });
        };
        surface.on("pointertap", handler);
        return () => {
          if (!surface.destroyed) surface.off("pointertap", handler);
        };
      },
    },
    spinCoordinator,
    tapTargets: input.canvasTaps,
  }).start();
  if (replayMode) {
    bar.enterReplayMode();
    chrome.menu.setMenuOpen(false);
    chrome.autoSpinPopup.hide();
    chrome.speedPopup.hide();
    chrome.header.setVisible(false);
    balancePresenter.hide();
    bar.gambleEntry.setShown(false);
  }
  return controlBar;
}
